import React, { useState } from 'react'
import {
  Avatar,
  Box,
  Chip,
  Collapse,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Switch,
  Badge,
} from '@material-ui/core'
import { AvatarGroup } from '@material-ui/lab'
import GetAppIcon from '@material-ui/icons/GetApp'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import { SubLink } from 'containers'
import { Translate } from 'react-localize-redux'
import binIcon from 'assets/images/16px/bin@1x.svg'
import editIcon from 'assets/images/16px/edit@1x.svg'
import { formatCostumDateMonthNoForUI } from 'utils/formatDate'
import PropTypes from 'prop-types'
import useTeamFeature from 'hooks/useTeamFeature'
import LockIcon from '@material-ui/icons/Lock'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import exportTaskGroupToExcel from './api'
import { TRAININGS_GAME } from 'constants/calender'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const useRowStyles = makeStyles((theme) => ({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
  link: {
    display: 'block',
    cursor: 'pointer',
  },
  lockBadgeOnIconButton: {
    '& .MuiBadge-badge': {
      width: 12,
      height: 12,
      minWidth: 12,
      backgroundColor: theme.palette.background.paper,
    },
    '& .MuiBadge-anchorOriginTopRightCircular .MuiSvgIcon-root': {
      fontSize: 10,
    },
  },
}))

const TaskGroupEventsCollapsibleRow = ({
  taskGroup,
  players,
  teamName,
  handleOpen,
  openAlertBox,
  handleAlertOpenForCompleteTask,
}) => {
  const [open, setOpen] = useState(false)
  const classes = useRowStyles()
  const featureGuard = useFeatureAccessGuard()

  const { enabled: hasTaskGroupExportEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.task_group_export_enabled
  )

  const renderCategory = (tempTaskGroup) => {
    let taskType = ''
    if (
      tempTaskGroup?.event_type?.includes(TRAININGS_GAME.training_camel_case)
    ) {
      taskType = TRAININGS_GAME.training
    } else if (
      tempTaskGroup?.event_type?.includes(TRAININGS_GAME.match_camel_case)
    ) {
      taskType = TRAININGS_GAME.match
    }
    switch (taskType) {
      case TRAININGS_GAME.training:
        return <Chip label={<Translate id="menu.training" />} color="primary" />
      case TRAININGS_GAME.match:
        return (
          <Chip label={<Translate id="menu.match-day" />} color="secondary" />
        )
      default:
        return <Chip label={<Translate id="task-management.general-cat" />} />
    }
  }

  const handleExport = async (taskGroupUuid, taskName) => {
    if (featureGuard(hasTaskGroupExportEnabled)) return

    try {
      const response = await exportTaskGroupToExcel(taskGroupUuid)
      const blob = new Blob([response], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${taskName}_${teamName}_CB.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error exporting task group', error)
    }
  }

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <SubLink
            to={`/task-group/${taskGroup.uuid}`}
            className={classes.link}
            hash={
              taskGroup?.relationships?.events?.length === 1
                ? `#${taskGroup?.relationships?.events[0].uuid}`
                : null
            }
          >
            {taskGroup.title}
          </SubLink>
        </TableCell>
        <TableCell>
          <SubLink
            to={`/task-group/${taskGroup.uuid}`}
            className={classes.link}
            hash={
              taskGroup?.relationships?.events?.length === 1
                ? `#${taskGroup?.relationships?.events[0].uuid}`
                : null
            }
          >
            {renderCategory(taskGroup)}{' '}
          </SubLink>
        </TableCell>
        <TableCell>
          <SubLink
            to={`/task-group/${taskGroup.uuid}`}
            className={classes.link}
            hash={
              taskGroup?.relationships?.events?.length === 1
                ? `#${taskGroup?.relationships?.events[0].uuid}`
                : null
            }
          >
            <AvatarGroup>
              {taskGroup?.person_uuids.map((personUuid) =>
                players
                  .filter((player) => player.uuid === personUuid)
                  .map((player) => (
                    <Avatar
                      key={player.id}
                      alt={player.first_name}
                      src={player.avatar?.full_url}
                    />
                  ))
              )}
            </AvatarGroup>
          </SubLink>
        </TableCell>
        <TableCell>
          {!taskGroup.completed && (
            <Tooltip
              title={<Translate id="button.edit" />}
              placement="top"
              arrow
            >
              <IconButton
                onClick={() => handleOpen(taskGroup)}
                disabled={Boolean(taskGroup.completed)}
              >
                <img src={editIcon} alt={editIcon} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip
            title={<Translate id="button.delete" />}
            placement="top"
            arrow
          >
            <IconButton onClick={() => openAlertBox(taskGroup.uuid)}>
              <img src={binIcon} alt={binIcon} />
            </IconButton>
          </Tooltip>

          {!taskGroup.completed && (
            <Tooltip
              title={<Translate id="button.complete-task" />}
              placement="top"
              arrow
            >
              <Switch
                checked={Boolean(taskGroup.completed)}
                onChange={() => handleAlertOpenForCompleteTask(taskGroup.uuid)}
                color="primary"
                name="completeTask"
                disabled={Boolean(taskGroup.completed)}
                inputProps={{ 'aria-label': 'primary checkbox' }}
              />
            </Tooltip>
          )}
          <Tooltip
            title={<Translate id="button.download" />}
            placement="top"
            arrow
          >
            <IconButton
              onClick={() => handleExport(taskGroup.uuid, taskGroup.title)}
              size="small"
              color="secondary"
            >
              <Badge
                className={classes.lockBadgeOnIconButton}
                badgeContent={
                  !hasTaskGroupExportEnabled ? (
                    <LockIcon color="primary" fontSize="small" />
                  ) : null
                }
                overlap="circular"
              >
                <GetAppIcon size="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ padding: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {!taskGroup.event_type.toLowerCase().includes('event') ? (
              <Box margin={1.8}>
                <Typography
                  variant="h6"
                  component="h6"
                  style={{ marginBottom: '0.5rem' }}
                >
                  <Translate id="task-management.event-details" />
                </Typography>
                <Table aria-label="events-details" stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="15%">
                        <Translate id="button.event" />
                      </TableCell>
                      <TableCell width="15%">
                        <Translate id="task-management.event-date" />
                      </TableCell>
                      <TableCell width="15%">
                        <Translate id="task-management.due-date" />
                      </TableCell>
                      <TableCell>
                        <Translate id="training.location" />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {taskGroup.relationships.events.map((event) => (
                      <TableRow key={event.uuid}>
                        <TableCell component="th" scope="row">
                          <SubLink
                            to={`/task-group/${taskGroup.uuid}`}
                            hash={`#${event.uuid}`}
                          >
                            {event.event_type}
                          </SubLink>
                        </TableCell>
                        <TableCell>
                          {formatCostumDateMonthNoForUI(event.date)}
                        </TableCell>
                        <TableCell>
                          {formatCostumDateMonthNoForUI(event.due_date)}
                        </TableCell>
                        <TableCell>{event.location}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography
                variant="h5"
                component="h5"
                style={{ margin: '1rem' }}
              >
                <Table aria-label="events-details" stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="5%">
                        <Translate id="task-management.task-date" />
                      </TableCell>
                      <TableCell width="5%">
                        <Translate id="task-management.due-date" />
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {taskGroup.relationships.events.map((event) => (
                      <TableRow key={event.uuid}>
                        <TableCell>
                          <SubLink
                            to={`/task-group/${taskGroup.uuid}`}
                            hash={`#${event.uuid}`}
                          >
                            {formatCostumDateMonthNoForUI(event.created_at)}
                          </SubLink>
                        </TableCell>
                        <TableCell>
                          {formatCostumDateMonthNoForUI(event.due_date)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Typography>
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}
TaskGroupEventsCollapsibleRow.propTypes = {
  taskGroup: PropTypes.shape().isRequired,
  players: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  teamName: PropTypes.string.isRequired,
  handleOpen: PropTypes.func.isRequired,
  openAlertBox: PropTypes.func.isRequired,
  handleAlertOpenForCompleteTask: PropTypes.func.isRequired,
}

export default TaskGroupEventsCollapsibleRow
